import 'bootstrap/dist/css/bootstrap.min.css';
import _ from 'lodash';
import { useEffect, useState } from 'react';
import { Container, Navbar, Table } from 'react-bootstrap';
import apiService from '../services/ApiService';
import { useTheme } from '../theme/ThemeContext';

export interface ResultTime {
  id: number;
  resultTime: string;
}

export interface Game {
  id: number;
  name: string;
  digitSupported: boolean;
  singlePattiSupported: boolean;
  burfiSupported: boolean;
  offline: boolean;
  status: string;
}

// Define types for result data
export interface Result {
  resultId: number;
  resultDate: string;
  resultTime?: ResultTime;
  gameDTO?: Game;
  singleResult: string;
  pattiResult: string;
  digitResult: string;
  burfiResult: string;
}

const groupBySiteName = (data: Result[]): { [key: string]: Result[] } => {
  return data.reduce((groups: { [key: string]: Result[] }, item: Result) => {
    if (!item.gameDTO?.name) return groups;
    const group = groups[item.gameDTO.name] || [];
    group.push(item);
    groups[item.gameDTO.name] = group;
    return groups;
  }, {});
};

const renderResult = (result: Result) => {
  return `${result.pattiResult}*${result.singleResult}`;
};

// Function to parse time string (e.g., '9:30 AM') into minutes since midnight for sorting
const timeToMinutes = (timeStr: string) => {
  if (!timeStr) return 0;
  const [time, period] = timeStr.split(' ');
  let [hours, minutes] = time.split(':').map(Number);
  if (period === 'PM' && hours < 12) hours += 12;
  if (period === 'AM' && hours === 12) hours = 0;
  return hours * 60 + minutes;
};

const ResultPage = () => {
  const theme = useTheme();
  const [dataSourceTodayResult, setDataSourceTodayResult] = useState<Result[]>([]);
  const [dataSourceCurrentResult, setDataSourceCurrentResult] = useState<Result>();
  const [dataSourcePrevResult, setDataSourcePrevResult] = useState<Result[]>([]);
  const message = process.env.REACT_APP_DISCLAIMER || 'Welcome to Smartmumbai Matka!';

  const bg = 'rgb(252, 76, 78)';  // Updated to black background
  const bgTextColor = 'white';  // White text color
  const tableBg = '#6c5c58';  // example table background
  const tableText = 'white';  // White text color for tables
  const footerBg = '#343a40';  // footer background color
  const footerText = theme.colors.white;  // footer text color
  const disclaimerBg = theme.colors.grey;  // disclaimer background color
  const disclaimerText = theme.colors.white;  // disclaimer text color
  const siteId = parseInt(process.env.REACT_APP_SITEID || '1');

  useEffect(() => {
    // Fetch data using the apiService
    apiService.getTodayResult(siteId)
      .then(data => setDataSourceTodayResult(data))
      .catch(error => console.error('Error fetching today\'s results:', error));

    apiService.getPreviousResult(siteId, 7)
      .then(data => setDataSourcePrevResult(data))
      .catch(error => console.error('Error fetching last day\'s results:', error));

    apiService.getCurrentResult(siteId)
      .then(data => setDataSourceCurrentResult(data))
      .catch(error => console.error('Error fetching last day\'s results:', error));
  }, [siteId]);

  const groupedData = _.groupBy(dataSourcePrevResult, 'resultDate');

  return (
    <div style={{ backgroundColor: bg, color: bgTextColor }}>
      <Navbar bg="dark" variant="dark" className="mb-4" style={{ justifyContent: 'space-between', padding: '1rem 2rem' }}>
        <Navbar.Brand className='font-weight-bold' style={{ color: '#fff', fontWeight: '600' }}>Mumbai Matka</Navbar.Brand>
      </Navbar>

      <Container>
        {/* Today Result */}
        <section className="mb-5">

          <div className="column p-3 d-flex flex-column align-items-center">
            <h5 className='font-weight-bold mb-1 text-center'>CURRENT RESULT</h5>
            <div>Time: {dataSourceCurrentResult?.resultTime?.resultTime}</div>
            <div>Result: {dataSourceCurrentResult?.pattiResult}*{dataSourceCurrentResult?.singleResult}</div>
          </div>

          <h5 className='font-weight-bold mb-3 text-center mt-5'>Today Result</h5>
          <section className="mb-5">
            {Object.entries(groupBySiteName(dataSourceTodayResult)).map(([siteName, results]) => {
              // Sort results by resultTime
              const sortedResults = [...results].sort((a, b) => {
                const timeA = a.resultTime?.resultTime ? timeToMinutes(a.resultTime.resultTime) : 0;
                const timeB = b.resultTime?.resultTime ? timeToMinutes(b.resultTime.resultTime) : 0;
                return timeA - timeB;
              });

              return (
                <div key={siteName}>
                  <div className="table-responsive">
                    <Table bordered striped>
                      <thead>
                        <tr>
                          <th>Date</th>
                          {sortedResults.map((result) => (
                            <th key={result.resultId}>{result.resultTime?.resultTime}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td>{new Date(sortedResults[0]?.resultDate).toLocaleDateString('en-GB')}</td>
                          {sortedResults.map((result) => (
                            <td key={result.resultId}>
                              {result.gameDTO?.burfiSupported ? renderResult(result) : `${result.pattiResult}*${result.singleResult}`}
                            </td>
                          ))}
                        </tr>
                      </tbody>
                    </Table>
                  </div>
                </div>
              );
            })}
          </section>
        </section>

        {/* Last Day Result */}
        <section className="mb-5">
          <h5 className='font-weight-bold mb-3 text-center'>Previous Week Results</h5>
          {Object.keys(groupedData).map((date, index) => (
            <div key={index} className="table-responsive">
              <Table striped bordered hover responsive className="w-100">
                <thead style={{ backgroundColor: tableBg, color: tableText }}>
                  <tr className="font-weight-bold">
                    <th>Date</th>
                    {groupedData[date].map((item, idx) => (
                      <th key={idx}> {item.resultTime?.resultTime}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  <tr style={{ backgroundColor: tableBg, color: tableText }}>
                    <td>{new Date(date).toLocaleDateString('en-GB')}</td>
                    {groupedData[date].map((item, idx) => (
                      <td key={idx}>
                        {item.gameDTO?.burfiSupported
                          ? item.burfiResult
                          : `${item.pattiResult}*${item.singleResult}`}
                      </td>
                    ))}
                  </tr>
                </tbody>
              </Table>
            </div>
          ))}
        </section>

      </Container>

      {/* Footer */}
      <footer style={{ backgroundColor: footerBg, color: footerText }}>
        <Container className="text-center py-3">
          <p>Copyright © smartmumbaimatka.com 2020. All rights reserved.</p>
        </Container>
        <Navbar style={{ backgroundColor: disclaimerBg, color: disclaimerText }}>
          <Navbar.Text>
            <div className="marquee">
              {message}
            </div>
          </Navbar.Text>
        </Navbar>
      </footer>

      {/* CSS for Scrolling Text */}
      <style>{`
        .marquee {
          display: inline-block;
          white-space: nowrap;
          animation: marquee 10s linear infinite;
        }

        @keyframes marquee {
          from {
            transform: translateX(100%);
          }
          to {
            transform: translateX(-100%);
          }
        }
      `}</style>
    </div>
  );
};

export default ResultPage;
